import { Shirt, Scissors } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState, useEffect } from "react";
import { STYLE_CODE_PREFIX, getStyleCode, formatStyleLabel } from "@/data/styleCodes";
import { additionalFieldOptions, overridesLoaded, subscribeOverrides } from "@/data/rcCodeOverrides";
import ButtonStyleSelect from "@/components/ButtonStyleSelect";
import CustomStyleCodes from "@/components/CustomStyleCodes";
import LiningMatchRow from "@/components/LiningMatchRow";
import SectionLiningRow from "@/components/SectionLiningRow";
import InnerFacingPipingRow from "@/components/InnerFacingPipingRow";
import LoadStylePresetButton from "@/components/LoadStylePresetButton";
import AutoStyleFields from "@/components/AutoStyleFields";
import ShoulderPadPair from "@/components/ShoulderPadPair";
import ShoulderPosturePair from "@/components/ShoulderPosturePair";
import PantTuxedoToggle, { isPantTuxedoActive } from "@/components/PantTuxedoToggle";
import PantFrontButtonRow from "@/components/PantFrontButtonRow";
import StyleSubSection from "@/components/StyleSubSection";
import useApplyStyleDefaults from "@/hooks/useApplyStyleDefaults";
import { JACKET_MAX_DEFAULTS, PANT_MAX_DEFAULTS } from "@/data/styleDefaults";
import { GLYPH_SETS } from "@/components/StyleIconCard";

// Jacket Styling Options
const JACKET_BODY_STYLES = ["Slim", "Regular", "Classic", "Relaxed"];  // kept for legacy state, no longer rendered
const JACKET_CANVAS_CONSTRUCTIONS = [
  "Full Canvas", "Half Canvas", "Fused", "Medium Fused",
  "Full Handmade & Full Canvas", "Half Handmade & Full Canvas",
  "Full Handmade & Half Canvas", "Half Handmade & Half Canvas",
  "Unconstructed Canvas", "Full Handmade Unconstructed", "Half Handmade Unconstructed",
  "Single Chest + Single Shoulder Pad", "Single Chest + Regular Shoulder Pad",
];
const JACKET_FRONT_CLOSURES = [
  "1-Button", "2-Button", "3-Button", "3-Roll-2", "4-Button",
  "2x1 DB", "4x2 DB", "6x1 DB", "6x2 DB", "8x2 DB"
];
const LAPEL_TYPES = ["Notch", "Peak", "Peak Lapel", "Extended Peak", "Shawl"];
const LAPEL_WIDTHS = [
  "2.5in (6.4cm)", "3in (7.6cm)", "3.5in (8.9cm)", 
  "3.75in (9.5cm)", "4in (10.2cm)", "4.5in (11.4cm)",
  "5in (12.7cm)", "5.5in (14cm)", "6in (15.2cm)"
];
const WAIST_POCKET_TYPES = [
  "Welt", "Flap", "Patch", "Double Besom", "No Pocket",
  "Normal Pocket + Ticket Pocket",
  "Normal Slanted Pocket + Ticket Pocket",
];
const WAIST_POCKET_ANGLES = ["Straight", "Slanted"];
const TICKET_POCKETS = ["None", "Welt", "Flap", "Patch", "Double Besom"];
const CHEST_POCKETS = ["None", "Welt", "Patch", "Double Besom"];
const BUTTON_STYLES = ["Horn", "Corozo", "Mother of Pearl", "Metal"];
const EXTERIOR_STITCHES = ["None", "1/16\" Pic", "5/16\" Pic", "1/16\" Top", "5/16\" Top", "1/4\" Double Top", "1/4\" Double Pic"];
const SLEEVE_BUTTONHOLES = ["4 Kissing", "4 Non-Kissing", "3 Kissing", "3 Non-Kissing", "No Buttons"];
const VENTS = ["Single Vent", "Double Vent", "No Vent"];
const LAPEL_BUTTONHOLES = ["None", "Working Buttonhole", "Milanese Hole", "Keyhole"];
const GORGE_LINES = ["Standard", "+1cm (0.4in)", "-2cm (0.8in)", "-5cm (2in)"];

// NEW: Chest Pocket Posture (7 codes)
const CHEST_POCKET_POSTURES = [
  "Standard", 
  "+0.5cm (0.2in) Higher", "+1cm (0.4in) Higher", "+1.5cm (0.6in) Higher",
  "-0.5cm (0.2in) Lower", "-1cm (0.4in) Lower", "-1.5cm (0.6in) Lower"
];

// NEW: Lower Pocket Flap Width (11 codes)
const LOWER_POCKET_FLAP_WIDTHS = [
  "Standard 5cm (2in)", 
  "4cm (1.6in)", "4.5cm (1.8in)", "5.5cm (2.2in)", "6cm (2.4in)", "6.5cm (2.6in)",
  "7cm (2.8in)", "7.5cm (3in)", "8cm (3.1in)", "8.5cm (3.3in)", "9cm (3.5in)"
];

// NEW: Ticket Pocket Posture (9 codes)
const TICKET_POCKET_POSTURES = [
  "Standard",
  "+0.5cm (0.2in) Higher", "+1cm (0.4in) Higher", "+1.5cm (0.6in) Higher", "+2cm (0.8in) Higher",
  "-0.5cm (0.2in) Lower", "-1cm (0.4in) Lower", "-1.5cm (0.6in) Lower", "-2cm (0.8in) Lower"
];

// NEW: Back Vent Length Adjustment (13 codes)
const BACK_VENT_LENGTHS = [
  "Standard",
  "+1cm (0.4in)", "+2cm (0.8in)", "+3cm (1.2in)", "+4cm (1.6in)", "+5cm (2in)", "+6cm (2.4in)",
  "-1cm (0.4in)", "-2cm (0.8in)", "-3cm (1.2in)", "-4cm (1.6in)", "-5cm (2in)", "-6cm (2.4in)"
];

// NEW: Armhole Adjustment (5 codes)
const ARMHOLE_ADJUSTMENTS = [
  "Standard",
  "+1cm (0.4in) Higher", "+2cm (0.8in) Higher",
  "-1cm (0.4in) Lower", "-2cm (0.8in) Lower"
];

// NEW: Shoulder Posture Adjustment (10 codes)
const SHOULDER_POSTURE_ADJUSTMENTS = [
  "Standard",
  "Square +0.5cm (0.2in)", "Square +1cm (0.4in)", "Square +1.5cm (0.6in)", "Square +2cm (0.8in)",
  "Sloped -0.5cm (0.2in)", "Sloped -1cm (0.4in)", "Sloped -1.5cm (0.6in)", "Sloped -2cm (0.8in)",
  "Forward Posture"
];

// Pants Styling Options
const PANT_BODY_STYLES = ["Slim", "Regular", "Classic", "Relaxed"];
const PANT_FRONT_STYLES = [
  "Flat Front",
  "Plain Front w/ Single Dart",
  "Plain Front w/ Double Darts",
  "Single Pleat",
  "Single Pleat fwd Fly",
  "Double Pleat",
  "Double Pleats fwd Sideseam",
  "Triple Pleat",
  "One Opposite Pleat",
  "One Box Pleat",
];
const PANT_PLEAT_DEPTHS = ["—", "1.5cm", "2.0cm", "2.5cm", "3.2cm", "3.8cm", "4.4cm"];
const FRONT_POCKETS = [
  "Slant", "2.0cm Slant", "2.5cm Slant", "3.2cm Slant", "5.1cm Slant", "Very Slant",
  "On-Seam", "Sideseam", "Double Besom", "Welt", "Front Welt w/ Zipper",
  "Square Jeans", "Round Jeans", "Arc Jeans", "Diamond Jeans", "DL Jeans", "No Pocket",
];
const FRONT_POCKET_BARTACKS = ["None", "L Shape", "D Bartack", "Dash (-) Bartack", "X Bartack"];
const FRONT_POCKET_STITCHES = [
  "None", "0.15 Top Stitch", "0.15 Pick Stitch", "0.3 Top Stitch",
  "0.6 Top Stitch", "0.6 Pick Stitch", "0.6cm Wide Pick Stitch",
];
const WATCH_POCKETS = [
  "None", "Normal Left", "Normal Right", "Normal Both",
  "Besom Left", "Welt Right", "Double Besom Left", "Double Besom Right", "Double Besom Both",
  "Right + Diamond Flap", "Right + Triangle Flap", "Right + Square Flap",
  "Left + Diamond Flap", "Left + Square Flap",
];
const INNER_WATCH_POCKETS = ["None", "Left", "Right", "Both", "ETQ w/ Zip Inside"];
const BACK_POCKET_CLOSURES = ["Button", "No Button", "Tab & Button", "Pointed Tab & Button", "Square Tab & Button", "Hidden Button", "Zipper", "Loop & Button"];
const BOTTOM_FINISHINGS = ["Plain Hem", "Cuff 1.5\"", "Cuff 1.75\"", "Cuff 2\""];
const BACK_POCKET_STYLES = [
  "No Pocket",
  "Double Besom",
  "Double Besom + Button",
  "Double Besom + Tab + Button",
  "Double Besom + Pointed Tab + Button",
  "Double Besom Hidden Button",
  "Welt",
  "Welt + Loop + Button",
  "Welt + Zipper",
  "Besom Back",
  "Back w/ Flap",
  "Back w/ Diamond Flap",
  "Back w/ Diamond Flap + Button",
  "Back w/ Pointed Flap",
  "Back w/ Pointed Flap + Button",
  "Pointed Patch",
  "Round Patch",
  "Round Patch + Button",
  "Knife Shape + Button",
  "Back Zipper",
];
const FRONT_CLOSURES_PANT = ["Button & Tab", "Hook & Bar", "Extended Tab"];
const WAISTBAND_STYLES = [
  "Standard",
  "Front & Back Continuous",
  "Uncontinued Front + Continued Back",
  "Front Continued",
  "Side Tabs",
  "Belt Loops",
];
const WAISTBAND_WIDTHS = [
  "1in (2.5cm)", "1.5in (3.8cm)", "2in (5.1cm)", "2.5in (6.4cm)", 
  "3in (7.6cm)", "3.5in (8.9cm)", "4in (10.2cm)", "4.5in (11.4cm)", "5in (12.7cm)"
];

// Vest Styling Options
const VEST_LAPELS = ["None", "Notch", "Peak", "Shawl"];
const VEST_FRONT_CLOSURES = ["5-Button", "6-Button", "4-Button", "DB 6x3"];
const VEST_POCKET_TYPES = ["Welt", "Patch", "Double Besom Pocket", "No Pockets"];
const VEST_POCKET_NUMBERS = ["2 Pockets", "4 Pockets"];
const VEST_BACKS = ["Same Fabric", "Lining", "Belt Back"];

// Style Select Component (renders RC factory code beside the option name)
const StyleSelect = ({ label, value, onChange, options, codePrefix, iconKind }) => {
  // Re-render when admin overrides hydrate so newly-added codes show up live.
  const [, tick] = useState(0);
  useEffect(() => subscribeOverrides(() => tick((n) => n + 1)), []);
  const extras = codePrefix && overridesLoaded() ? additionalFieldOptions(codePrefix, options) : [];
  const mergedOptions = extras.length ? [...options, ...extras] : options;
  const selected = value || mergedOptions[0];
  const glyphs = iconKind ? GLYPH_SETS[iconKind] || {} : null;
  const Glyph = ({ opt, size = 16 }) => {
    if (!glyphs) return null;
    // Try exact label, then bi-directional substring, then token-overlap match
    // (handles "Cuff 1.5\"" vs key '1.5" Cuff' — same tokens, different order).
    const tokenize = (s) => String(s || "").toLowerCase().split(/[\s/\-_,]+/).filter(Boolean);
    const direct = glyphs[opt];
    let g = direct;
    if (!g) {
      const norm = String(opt || "").toLowerCase();
      const optTokens = new Set(tokenize(opt));
      const key = Object.keys(glyphs).find((k) => {
        const lk = k.toLowerCase();
        if (norm.includes(lk) || lk.includes(norm)) return true;
        const keyTokens = tokenize(k);
        // Match if every token in the glyph key is present in the option
        return keyTokens.length > 0 && keyTokens.every((t) => optTokens.has(t));
      });
      if (key) g = glyphs[key];
    }
    if (!g) return null;
    return (
      <span className="inline-flex items-center justify-center text-amber-300 shrink-0" style={{ width: size, height: size }}>
        {g}
      </span>
    );
  };
  return (
    <div>
      <label className="text-xs text-slate-400 block mb-1">{label}</label>
      <Select value={selected} onValueChange={onChange}>
        <SelectTrigger className="bg-[#0a1628] border-white/10 h-8 text-xs">
          <SelectValue>
            <span className="flex items-center gap-1.5 truncate">
              <Glyph opt={selected} size={14} />
              <span className="truncate">{selected}</span>
              {(() => {
                const c = getStyleCode(codePrefix, mergedOptions, selected);
                return c ? <code className="text-[10px] text-amber-400/80 font-mono shrink-0">{c}</code> : null;
              })()}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {mergedOptions.map((opt) => {
            const c = getStyleCode(codePrefix, mergedOptions, opt);
            const isExtra = extras.includes(opt);
            return (
              <SelectItem key={opt} value={opt} className="text-xs">
                <span className="flex items-center gap-2 w-full">
                  <Glyph opt={opt} size={16} />
                  <span className="flex-1">{opt}{isExtra && <span className="text-amber-400 ml-1 text-[9px]">★</span>}</span>
                  {c && <code className="text-[10px] text-amber-400/80 font-mono">{c}</code>}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};

// ============= SHIRT STYLING OPTIONS =============
const SHIRT_COLLAR_STYLES = [
  "Spread", "Cutaway", "Wide Spread", "Semi Spread", "Standard",
  "Button-Down", "Tab Collar", "Pin Collar", "Club", "Band", "Wing", "Cuban", "Hidden Placket",
];
const SHIRT_COLLAR_HEIGHTS = ["Standard 4cm", "3.5cm", "4.5cm", "5cm"];
const SHIRT_COLLAR_STAYS = ["Removable", "Sewn-In", "No Stays"];
const SHIRT_CUFF_STYLES = [
  "Single Button Barrel", "Two Button Barrel", "Convertible", "French",
  "Mitered Barrel", "Rounded Barrel", "Square Barrel", "Angled Barrel",
];
const SHIRT_PLACKETS = ["Standard", "French Placket", "Hidden Placket", "Pleat Front", "Tuxedo Bib"];
const SHIRT_POCKETS = ["None", "Standard", "Standard w/ Flap", "Rounded", "Pointed", "Two Pockets", "Western", "With Pen Slot"];
const SHIRT_BACKS = ["Plain", "Side Pleats", "Box Pleat", "Darts", "Yoke Split"];
const SHIRT_BOTTOMS = ["Curved", "Straight", "Side Slit"];
const SHIRT_MONOGRAMS = ["None", "Left Cuff", "Right Cuff", "Chest", "Pocket", "Bottom"];

const MTMStylesStep = ({
  includeJacket,
  includeTrouser,
  includeVest,
  includeShirt,
  garmentType,
  // Jacket styling
  jacketStyling,
  setJacketStyling,
  jacketStylingRemarks,
  setJacketStylingRemarks,
  // Pant styling
  pantStyling,
  setPantStyling,
  pantStylingRemarks,
  setPantStylingRemarks,
  // Vest styling
  vestStyling,
  setVestStyling,
  vestStylingRemarks,
  setVestStylingRemarks,
  // Shirt styling
  shirtStyling,
  setShirtStyling,
  shirtStylingRemarks,
  setShirtStylingRemarks,
  // Rush order
  rushOrder,
  setRushOrder,
  orderNotes,
  setOrderNotes,
}) => {
  const isTuxedo = garmentType === "Tuxedo" || (garmentType || "").toLowerCase().includes("tux");

  // Auto-default factory finishing fields on first mount (only fills blanks —
  // never overwrites a value the user or a loaded preset already set).
  useApplyStyleDefaults(jacketStyling, setJacketStyling, JACKET_MAX_DEFAULTS);
  useApplyStyleDefaults(pantStyling, setPantStyling, PANT_MAX_DEFAULTS);
  // Side Tabs auto-binding: when waistband_style is "Side Tabs", T-3329 (No belt loop)
  // and T-3352 (Adjust buckle QZMAB01-2) ride along automatically — they're a factory
  // package that always travels together.
  const SIDE_TAB_CODES = [
    { code: "T-3329", label: "No belt loop",                  field: "pant_belt_loop_style" },
    { code: "T-3352", label: "Adjust buckle QZMAB01-2",       field: "pant_waist_adjustment" },
  ];
  useEffect(() => {
    if (!pantStyling || !setPantStyling) return;
    const isSideTabs = pantStyling.waistband_style === "Side Tabs";
    const current = pantStyling.custom_codes || [];
    const hasSideTabCodes = SIDE_TAB_CODES.every((c) => current.some((x) => x.code === c.code));
    if (isSideTabs && !hasSideTabCodes) {
      // Inject missing side-tab companion codes
      const toAdd = SIDE_TAB_CODES
        .filter((c) => !current.some((x) => x.code === c.code))
        .map((c) => ({ id: `${c.code}-sidetabs`, code: c.code, label: c.label, field: c.field, auto: true }));
      setPantStyling({ ...pantStyling, custom_codes: [...current, ...toAdd] });
    } else if (!isSideTabs && current.some((x) => x.auto && SIDE_TAB_CODES.some((c) => c.code === x.code))) {
      // Remove auto-injected codes when waistband no longer Side Tabs
      setPantStyling({
        ...pantStyling,
        custom_codes: current.filter((x) => !(x.auto && SIDE_TAB_CODES.some((c) => c.code === x.code))),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pantStyling?.waistband_style]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold mb-2">Styles</h2>
        <p className="text-slate-400">Configure styling options for each garment</p>
      </div>

      {/* Jacket Styling */}
      {includeJacket && (
        <div id="pos-styling-jacket" className="bg-[#111c2e] rounded-xl p-4 border border-white/10 scroll-mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold flex items-center gap-2 text-amber-400">
              <Shirt size={18} /> Jacket Styling
            </h3>
            <LoadStylePresetButton
              section="jacket"
              jacketStyling={jacketStyling}
              setJacketStyling={setJacketStyling}
              testId="pos-load-jacket-style"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <StyleSelect 
              label="Canvas Construction" 
              value={jacketStyling.canvas_construction} 
              onChange={(v) => setJacketStyling({...jacketStyling, canvas_construction: v})} 
              options={JACKET_CANVAS_CONSTRUCTIONS} codePrefix="canvas_construction"
            />
            <StyleSelect 
              label="Front Closure" 
              value={jacketStyling.front_closure} 
              onChange={(v) => setJacketStyling({...jacketStyling, front_closure: v})} 
              options={JACKET_FRONT_CLOSURES} codePrefix="front_closure" 
            />
            <StyleSelect 
              label="Lapel Type" 
              value={jacketStyling.lapel_type} 
              onChange={(v) => setJacketStyling({...jacketStyling, lapel_type: v})} 
              options={LAPEL_TYPES} codePrefix="lapel_type" 
              iconKind="lapel"
            />
            <StyleSelect 
              label="Lapel Width" 
              value={jacketStyling.lapel_width} 
              onChange={(v) => setJacketStyling({...jacketStyling, lapel_width: v})} 
              options={LAPEL_WIDTHS} codePrefix="lapel_width" 
            />
            <StyleSelect 
              label="Gorge Line" 
              value={jacketStyling.gorge_line} 
              onChange={(v) => setJacketStyling({...jacketStyling, gorge_line: v})} 
              options={GORGE_LINES} 
            />
            <StyleSelect 
              label="Waist Pocket" 
              value={jacketStyling.waist_pocket_type} 
              onChange={(v) => setJacketStyling({...jacketStyling, waist_pocket_type: v})} 
              options={WAIST_POCKET_TYPES} codePrefix="waist_pocket_type" 
              iconKind="pocket"
            />
            <StyleSelect 
              label="Pocket Angle" 
              value={jacketStyling.waist_pocket_angle} 
              onChange={(v) => setJacketStyling({...jacketStyling, waist_pocket_angle: v})} 
              options={WAIST_POCKET_ANGLES} codePrefix="waist_pocket_angle" 
            />
            <StyleSelect 
              label="Ticket Pocket" 
              value={jacketStyling.ticket_pocket} 
              onChange={(v) => setJacketStyling({...jacketStyling, ticket_pocket: v})} 
              options={TICKET_POCKETS} codePrefix="ticket_pocket" 
            />
            <StyleSelect 
              label="Chest Pocket" 
              value={jacketStyling.chest_pocket} 
              onChange={(v) => setJacketStyling({...jacketStyling, chest_pocket: v})} 
              options={CHEST_POCKETS} codePrefix="chest_pocket" 
            />
            <StyleSelect 
              label="Exterior Stitch" 
              value={jacketStyling.exterior_stitch} 
              onChange={(v) => setJacketStyling({...jacketStyling, exterior_stitch: v})} 
              options={EXTERIOR_STITCHES} codePrefix="exterior_stitch" 
            />
            <StyleSelect 
              label="Sleeve Buttonholes" 
              value={jacketStyling.sleeve_buttonholes} 
              onChange={(v) => setJacketStyling({...jacketStyling, sleeve_buttonholes: v})} 
              options={SLEEVE_BUTTONHOLES} codePrefix="sleeve_buttons" 
            />
            <StyleSelect 
              label="Vent" 
              value={jacketStyling.vent} 
              onChange={(v) => setJacketStyling({...jacketStyling, vent: v})} 
              options={VENTS} codePrefix="vent" 
              iconKind="vent"
            />
            <StyleSelect 
              label="Lining Style" 
              value={jacketStyling.lining_style} 
              onChange={(v) => setJacketStyling({...jacketStyling, lining_style: v})} 
              options={[]} codePrefix="lining_style" 
            />
            <StyleSelect 
              label="Lapel Buttonhole" 
              value={jacketStyling.lapel_buttonhole} 
              onChange={(v) => setJacketStyling({...jacketStyling, lapel_buttonhole: v})} 
              options={LAPEL_BUTTONHOLES} codePrefix="lapel_buttonhole" 
            />
            <StyleSelect 
              label="Chest Pocket Posture" 
              value={jacketStyling.chest_pocket_posture} 
              onChange={(v) => setJacketStyling({...jacketStyling, chest_pocket_posture: v})} 
              options={CHEST_POCKET_POSTURES} codePrefix="chest_pocket_posture" 
            />
            <StyleSelect 
              label="Lower Pocket Flap Width" 
              value={jacketStyling.lower_pocket_flap_width} 
              onChange={(v) => setJacketStyling({...jacketStyling, lower_pocket_flap_width: v})} 
              options={LOWER_POCKET_FLAP_WIDTHS} codePrefix="lower_pocket_flap_width" 
            />
            <StyleSelect 
              label="Ticket Pocket Posture" 
              value={jacketStyling.ticket_pocket_posture} 
              onChange={(v) => setJacketStyling({...jacketStyling, ticket_pocket_posture: v})} 
              options={TICKET_POCKET_POSTURES} codePrefix="ticket_pocket_posture" 
            />
            <StyleSelect 
              label="Back Vent Length" 
              value={jacketStyling.back_vent_length} 
              onChange={(v) => setJacketStyling({...jacketStyling, back_vent_length: v})} 
              options={BACK_VENT_LENGTHS} codePrefix="back_vent_length" 
            />
          </div>

          <LiningMatchRow
            jacketStyling={jacketStyling}
            setJacketStyling={setJacketStyling}
            pantStyling={pantStyling}
            setPantStyling={setPantStyling}
            vestStyling={vestStyling}
            setVestStyling={setVestStyling}
            testId="pos-jacket-lining-match"
          />

          <InnerFacingPipingRow
            value={jacketStyling.inner_facing_piping}
            onChange={(v) => setJacketStyling({ ...jacketStyling, inner_facing_piping: v })}
            testId="pos-jacket-inner-facing-piping"
          />

          {/* Monogram (its own section above Postures) */}
          <div className="mt-3 pt-3 border-t border-white/5">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Monogram</p>
            <div className="max-w-xs">
              <label className="text-[10px] text-slate-500 block mb-0.5">Under Collar Monogram</label>
              <Input
                value={jacketStyling.under_collar_monogram || ""}
                onChange={(e) => setJacketStyling({ ...jacketStyling, under_collar_monogram: e.target.value })}
                placeholder="Initials..."
                className="bg-[#0a1628] border-white/10 h-8 text-xs"
                data-testid="pos-jacket-monogram-input"
              />
            </div>
          </div>

          {/* Postures section — all body-shape adjustments live together */}
          <div className="mt-3 pt-3 border-t border-white/5" data-testid="pos-jacket-postures">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Postures</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              <StyleSelect
                label="Armhole Adjustment"
                value={jacketStyling.armhole_adjustment}
                onChange={(v) => setJacketStyling({ ...jacketStyling, armhole_adjustment: v })}
                options={ARMHOLE_ADJUSTMENTS} codePrefix="armhole_adjustment"
              />
              <ShoulderPosturePair
                styling={jacketStyling}
                setStyling={setJacketStyling}
                options={SHOULDER_POSTURE_ADJUSTMENTS}
                testId="pos-shoulder-posture"
              />
              <StyleSelect
                label="Slope Degree (溜肩程度)"
                value={jacketStyling.slope_shoulder_degree}
                onChange={(v) => setJacketStyling({ ...jacketStyling, slope_shoulder_degree: v })}
                options={[]} codePrefix="slope_shoulder_degree"
              />
              <StyleSelect
                label="Back Width Adjustment"
                value={jacketStyling.back_width_adjustment}
                onChange={(v) => setJacketStyling({ ...jacketStyling, back_width_adjustment: v })}
                options={[]} codePrefix="back_width_adjustment"
              />
              <StyleSelect
                label="Back Collar Adjustment (Lower collar)"
                value={jacketStyling.back_collar_adjustment}
                onChange={(v) => setJacketStyling({ ...jacketStyling, back_collar_adjustment: v })}
                options={[]} codePrefix="back_collar_adjustment"
              />
              <StyleSelect
                label="Front Chest Adjustment"
                value={jacketStyling.front_chest_adjustment}
                onChange={(v) => setJacketStyling({ ...jacketStyling, front_chest_adjustment: v })}
                options={[]} codePrefix="front_chest_adjustment"
              />
              <StyleSelect
                label="Front Waist Adjustment (前中腰围)"
                value={jacketStyling.front_waist_adjustment}
                onChange={(v) => setJacketStyling({ ...jacketStyling, front_waist_adjustment: v })}
                options={[]} codePrefix="front_waist_adjustment"
              />
              <ShoulderPadPair
                styling={jacketStyling}
                setStyling={setJacketStyling}
                StyleSelectComponent={StyleSelect}
                testId="pos-shoulder-pad"
              />
            </div>
          </div>

          {/* Jacket Allowances — extra seam tolerances paired together */}
          <StyleSubSection title="Jacket Allowances" testId="pos-jacket-allowances">
            <StyleSelect
              label="Sleeve Seam Adjustment"
              value={jacketStyling.sleeve_seam_adjustment}
              onChange={(v) => setJacketStyling({ ...jacketStyling, sleeve_seam_adjustment: v })}
              options={[]} codePrefix="sleeve_seam_adjustment"
            />
            <StyleSelect
              label="Pocket Seam Fabric"
              value={jacketStyling.pocket_seam_fabric}
              onChange={(v) => setJacketStyling({ ...jacketStyling, pocket_seam_fabric: v })}
              options={[]} codePrefix="pocket_seam_fabric"
            />
            <StyleSelect
              label="Back Side Seam"
              value={jacketStyling.back_side_seam}
              onChange={(v) => setJacketStyling({ ...jacketStyling, back_side_seam: v })}
              options={[]} codePrefix="back_side_seam"
            />
          </StyleSubSection>

          {/* Button Style — merged: single picker, parent factory code badge inline */}
          <div className="mt-3 pt-3 border-t border-white/5" data-testid="pos-jacket-button-style-section">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Button Style</p>
              <code className="text-[10px] text-amber-400/80 font-mono">C-0638</code>
              <span className="text-[10px] text-slate-500 italic">(Customer appointed button color)</span>
            </div>
            <div className="max-w-sm">
              <ButtonStyleSelect
                label=""
                category="suit"
                value={jacketStyling.button_style}
                onChange={(v) => setJacketStyling({ ...jacketStyling, button_style: v })}
                testId="pos-jacket-button-style"
              />
            </div>
          </div>

          <AutoStyleFields
            section="jacket"
            styling={jacketStyling}
            setStyling={setJacketStyling}
            excludeFields={[
              "body_style","canvas_construction","front_closure","lapel_type","lapel_width","gorge_line",
              "shoulder_pad","shoulder_pad_left","shoulder_pad_right",
              "shoulder_posture","shoulder_posture_left","shoulder_posture_right",
              "waist_pocket_type","waist_pocket_angle","ticket_pocket","chest_pocket",
              "button_style","exterior_stitch","sleeve_buttons","sleeve_slit_option","vent",
              "lining_style","lapel_buttonhole","chest_pocket_posture","lower_pocket_flap_width",
              "ticket_pocket_posture","back_vent_length","armhole_adjustment",
              "slope_shoulder_degree","back_width_adjustment",
              "back_collar_adjustment","lining_color","sleeve_lining_color","inner_facing_piping",
              "front_chest_adjustment","front_waist_adjustment",
              "under_collar_monogram",
              "sleeve_seam_adjustment","pocket_seam_fabric","back_side_seam",
              "front_button_code","button_style_code",
            ]}
            hideFieldsMatching={isTuxedo ? [] : ["tuxedo", "tux"]}
            testIdPrefix="pos-jacket-auto-style"
          />

          {/* Custom Style Codes */}
          <CustomStyleCodes
            bucket="jacket"
            value={jacketStyling.custom_codes || []}
            onChange={(v) => setJacketStyling({ ...jacketStyling, custom_codes: v })}
            testId="pos-jacket-custom-codes"
          />

          {/* Remarks */}
          <div className="mt-4">
            <label className="text-xs text-slate-400 block mb-1">Jacket Notes</label>
            <Textarea 
              value={jacketStylingRemarks || ""}
              onChange={(e) => setJacketStylingRemarks(e.target.value)}
              placeholder="Special jacket instructions..."
              className="bg-[#0a1628] border-white/10 h-16 text-sm resize-none"
            />
          </div>
        </div>
      )}

      {/* Pant Styling */}
      {includeTrouser && (
        <div id="pos-styling-pant" className="bg-[#111c2e] rounded-xl p-4 border border-white/10 scroll-mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold flex items-center gap-2 text-blue-400">
              <Scissors size={18} /> Trouser Styling
            </h3>
            <div className="flex items-center gap-2">
              <PantTuxedoToggle
                pantStyling={pantStyling}
                setPantStyling={setPantStyling}
                testId="pos-pant-tuxedo-toggle"
              />
              <LoadStylePresetButton
                section="pant"
                pantStyling={pantStyling}
                setPantStyling={setPantStyling}
                testId="pos-load-pant-style"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <StyleSelect 
              label="Body Style" 
              value={pantStyling.body_style} 
              onChange={(v) => setPantStyling({...pantStyling, body_style: v})} 
              options={PANT_BODY_STYLES} codePrefix="pant_body_style" 
            />
            <StyleSelect 
              label="Front Style" 
              value={pantStyling.front_style} 
              onChange={(v) => setPantStyling({...pantStyling, front_style: v})} 
              options={PANT_FRONT_STYLES} codePrefix="pant_front_style" 
            />
            <StyleSelect 
              label="Front Pocket" 
              value={pantStyling.front_pocket} 
              onChange={(v) => setPantStyling({...pantStyling, front_pocket: v})} 
              options={FRONT_POCKETS} codePrefix="pant_front_pocket" 
            />
            <StyleSelect 
              label="Back Pocket" 
              value={pantStyling.back_pocket_style} 
              onChange={(v) => setPantStyling({...pantStyling, back_pocket_style: v})} 
              options={BACK_POCKET_STYLES} codePrefix="pant_back_pocket_style" 
            />
            <StyleSelect 
              label="Back Closure" 
              value={pantStyling.back_pocket_closure} 
              onChange={(v) => setPantStyling({...pantStyling, back_pocket_closure: v})} 
              options={BACK_POCKET_CLOSURES} codePrefix="pant_back_pocket_closure" 
            />
            <StyleSelect 
              label="Bottom Finish" 
              value={pantStyling.bottom_finishing} 
              onChange={(v) => setPantStyling({...pantStyling, bottom_finishing: v})} 
              options={BOTTOM_FINISHINGS} codePrefix="pant_bottom_finishing" 
              iconKind="cuff"
            />
            <StyleSelect 
              label="Front Closure" 
              value={pantStyling.front_closure} 
              onChange={(v) => setPantStyling({...pantStyling, front_closure: v})} 
              options={FRONT_CLOSURES_PANT} codePrefix="pant_front_closure" 
            />
            <StyleSelect 
              label="Waistband Style" 
              value={pantStyling.waistband_style} 
              onChange={(v) => setPantStyling({...pantStyling, waistband_style: v})} 
              options={WAISTBAND_STYLES} codePrefix="pant_waistband_style" 
            />
            <StyleSelect 
              label="Waistband Height" 
              value={pantStyling.waistband_width} 
              onChange={(v) => setPantStyling({...pantStyling, waistband_width: v})} 
              options={WAISTBAND_WIDTHS} codePrefix="pant_waistband_width" 
            />
            <StyleSelect 
              label="Pleat Depth" 
              value={pantStyling.pleat_depth} 
              onChange={(v) => setPantStyling({...pantStyling, pleat_depth: v})} 
              options={PANT_PLEAT_DEPTHS} codePrefix="pant_pleat_depth" 
            />
            <StyleSelect 
              label="Watch Pocket" 
              value={pantStyling.watch_pocket} 
              onChange={(v) => setPantStyling({...pantStyling, watch_pocket: v})} 
              options={WATCH_POCKETS} codePrefix="pant_watch_pocket" 
            />
            <StyleSelect 
              label="Inner Watch Pocket" 
              value={pantStyling.inner_watch_pocket} 
              onChange={(v) => setPantStyling({...pantStyling, inner_watch_pocket: v})} 
              options={INNER_WATCH_POCKETS} codePrefix="pant_inner_watch_pocket" 
            />
            <StyleSelect 
              label="Front Pocket Bartack" 
              value={pantStyling.front_pocket_bartack} 
              onChange={(v) => setPantStyling({...pantStyling, front_pocket_bartack: v})} 
              options={FRONT_POCKET_BARTACKS} codePrefix="pant_front_pocket_bartack" 
            />
            <StyleSelect 
              label="Front Pocket Stitch" 
              value={pantStyling.front_pocket_stitch} 
              onChange={(v) => setPantStyling({...pantStyling, front_pocket_stitch: v})} 
              options={FRONT_POCKET_STITCHES} codePrefix="pant_front_pocket_stitch" 
            />
          </div>

          <SectionLiningRow
            value={pantStyling.pant_lining_color}
            onChange={(v) => setPantStyling({ ...pantStyling, pant_lining_color: v })}
            label="Pant Lining"
            testId="pos-pant-lining"
          />

          <PantFrontButtonRow
            pantStyling={pantStyling}
            setPantStyling={setPantStyling}
            testId="pos-pant-front-button"
          />

          {/* Pant Allowances — seam tolerances grouped */}
          <StyleSubSection title="Pant Allowances" testId="pos-pant-allowances">
            <StyleSelect
              label="Outer Seam Allowance"
              value={pantStyling.pant_outer_seam_allowance}
              onChange={(v) => setPantStyling({ ...pantStyling, pant_outer_seam_allowance: v })}
              options={[]} codePrefix="pant_outer_seam_allowance"
            />
            <StyleSelect
              label="Seam Requirement"
              value={pantStyling.pant_seam_requirement}
              onChange={(v) => setPantStyling({ ...pantStyling, pant_seam_requirement: v })}
              options={[]} codePrefix="pant_seam_requirement"
            />
          </StyleSubSection>

          {/* Pant Satin Details — only visible when Pant Tuxedo is active */}
          {isPantTuxedoActive(pantStyling) && (
          <StyleSubSection title="Pant Satin Details" testId="pos-pant-satin-details">
            <StyleSelect
              label="Waistband Fabric"
              value={pantStyling.pant_waistband_fabric}
              onChange={(v) => setPantStyling({ ...pantStyling, pant_waistband_fabric: v })}
              options={[]} codePrefix="pant_waistband_fabric"
            />
            <StyleSelect
              label="Satin Position"
              value={pantStyling.pant_satin_position}
              onChange={(v) => setPantStyling({ ...pantStyling, pant_satin_position: v })}
              options={[]} codePrefix="pant_satin_position"
            />
            <StyleSelect
              label="Back Pocket Besom Fabric"
              value={pantStyling.pant_back_pocket_besom_fabric}
              onChange={(v) => setPantStyling({ ...pantStyling, pant_back_pocket_besom_fabric: v })}
              options={[]} codePrefix="pant_back_pocket_besom_fabric"
            />
            <StyleSelect
              label="Front Pocket Opening"
              value={pantStyling.pant_front_pocket_opening}
              onChange={(v) => setPantStyling({ ...pantStyling, pant_front_pocket_opening: v })}
              options={[]} codePrefix="pant_front_pocket_opening"
            />
            <StyleSelect
              label="Satin Fabric Notice"
              value={pantStyling.pant_satin_fabric_notice}
              onChange={(v) => setPantStyling({ ...pantStyling, pant_satin_fabric_notice: v })}
              options={[]} codePrefix="pant_satin_fabric_notice"
            />
          </StyleSubSection>
          )}

          <AutoStyleFields
            section="pant"
            styling={pantStyling}
            setStyling={setPantStyling}
            excludeFields={[
              "pant_body_style","pant_front_style","pant_front_pocket",
              "pant_back_pocket_style","pant_back_pocket_closure",
              "pant_bottom_finishing","pant_front_closure","pant_waistband_style",
              "pant_waistband_width","pant_pleat_depth","pant_watch_pocket",
              "pant_inner_watch_pocket","pant_front_pocket_bartack",
              "pant_front_pocket_stitch","pant_lining_color",
              "pant_belt_loop_style",
              "pant_outer_seam_allowance","pant_seam_requirement",
              "pant_waistband_fabric","pant_satin_position",
              "pant_back_pocket_besom_fabric","pant_front_pocket_opening",
              "pant_satin_fabric_notice",
            ]}
            testIdPrefix="pos-pant-auto-style"
          />

          {/* Custom Style Codes */}
          <CustomStyleCodes
            bucket="pant"
            value={pantStyling.custom_codes || []}
            onChange={(v) => setPantStyling({ ...pantStyling, custom_codes: v })}
            testId="pos-pant-custom-codes"
          />

          {/* Remarks */}
          <div className="mt-4">
            <label className="text-xs text-slate-400 block mb-1">Trouser Notes</label>
            <Textarea 
              value={pantStylingRemarks || ""}
              onChange={(e) => setPantStylingRemarks(e.target.value)}
              placeholder="Special trouser instructions..."
              className="bg-[#0a1628] border-white/10 h-16 text-sm resize-none"
            />
          </div>
        </div>
      )}

      {/* Vest Styling */}
      {includeVest && (
        <div id="pos-styling-vest" className="bg-[#111c2e] rounded-xl p-4 border border-white/10 scroll-mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold flex items-center gap-2 text-purple-400">
              <Shirt size={18} /> Vest Styling
            </h3>
            <LoadStylePresetButton
              section="vest"
              vestStyling={vestStyling}
              setVestStyling={setVestStyling}
              testId="pos-load-vest-style"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <StyleSelect 
              label="Lapel" 
              value={vestStyling.lapel} 
              onChange={(v) => setVestStyling({...vestStyling, lapel: v})} 
              options={VEST_LAPELS} codePrefix="vest_lapel" 
            />
            <StyleSelect 
              label="Pocket Type" 
              value={vestStyling.pocket_type} 
              onChange={(v) => setVestStyling({...vestStyling, pocket_type: v})} 
              options={VEST_POCKET_TYPES} codePrefix="vest_pocket_type" 
            />
            <StyleSelect 
              label="Pocket Number" 
              value={vestStyling.pocket_number} 
              onChange={(v) => setVestStyling({...vestStyling, pocket_number: v})} 
              options={VEST_POCKET_NUMBERS} codePrefix="vest_pocket_number" 
            />
            <StyleSelect 
              label="Back" 
              value={vestStyling.back} 
              onChange={(v) => setVestStyling({...vestStyling, back: v})} 
              options={VEST_BACKS} codePrefix="vest_back" 
            />
            <StyleSelect
              label="Back Belt"
              value={vestStyling.back_belt}
              onChange={(v) => setVestStyling({ ...vestStyling, back_belt: v })}
              options={[]} codePrefix="vest_back_belt"
            />
          </div>

          {/* Front Button — own section with code badge */}
          <div className="mt-3 pt-3 border-t border-white/5" data-testid="pos-vest-front-button-section">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-[10px] uppercase tracking-wider text-slate-400">Front Button</p>
              <code className="text-[10px] text-amber-400/80 font-mono">V-4240</code>
              <span className="text-[10px] text-slate-500 italic">(button — customer appointed)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <ButtonStyleSelect
                label=""
                category="suit"
                value={vestStyling.front_button_code}
                onChange={(v) => setVestStyling({ ...vestStyling, front_button_code: v })}
                testId="pos-vest-front-button"
              />
              <StyleSelect
                label=""
                value={vestStyling.front_closure}
                onChange={(v) => setVestStyling({ ...vestStyling, front_closure: v })}
                options={VEST_FRONT_CLOSURES} codePrefix="vest_front_closure"
              />
            </div>
          </div>

          <SectionLiningRow
            value={vestStyling.vest_lining_color}
            onChange={(v) => setVestStyling({
              ...vestStyling,
              vest_lining_color: v,
              // V-4714 "customer appointed lining for inner part" auto-matches
              vest_inner_fabric: v ? "customer appointed lining for inner part" : vestStyling.vest_inner_fabric,
            })}
            label="Vest Lining"
            testId="pos-vest-lining"
          />

          <AutoStyleFields
            section="vest"
            styling={vestStyling}
            setStyling={setVestStyling}
            excludeFields={[
              "vest_lapel","vest_front_closure","vest_pocket_type",
              "vest_pocket_number","vest_back","vest_back_belt","vest_lining_color",
              "vest_inner_fabric","front_button_code",
            ]}
            testIdPrefix="pos-vest-auto-style"
          />

          {/* Custom Style Codes */}
          <CustomStyleCodes
            bucket="vest"
            value={vestStyling.custom_codes || []}
            onChange={(v) => setVestStyling({ ...vestStyling, custom_codes: v })}
            testId="pos-vest-custom-codes"
          />

          {/* Remarks */}
          <div className="mt-4">
            <label className="text-xs text-slate-400 block mb-1">Vest Notes</label>
            <Textarea 
              value={vestStylingRemarks || ""}
              onChange={(e) => setVestStylingRemarks(e.target.value)}
              placeholder="Special vest instructions..."
              className="bg-[#0a1628] border-white/10 h-16 text-sm resize-none"
            />
          </div>
        </div>
      )}

      {/* Shirt Styling */}
      {includeShirt && shirtStyling && setShirtStyling && (
        <div id="pos-styling-shirt" className="bg-[#111c2e] rounded-xl p-4 border border-white/10 scroll-mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold flex items-center gap-2 text-rose-400">
              <Shirt size={18} /> Shirt Styling
            </h3>
            <LoadStylePresetButton
              section="shirt"
              shirtStyling={shirtStyling}
              setShirtStyling={setShirtStyling}
              testId="pos-load-shirt-style"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            <StyleSelect label="Collar Style"   value={shirtStyling.collar_style}   onChange={(v) => setShirtStyling({...shirtStyling, collar_style: v})}   options={SHIRT_COLLAR_STYLES}   codePrefix="shirt_collar_style" />
            <StyleSelect label="Collar Height"  value={shirtStyling.collar_height}  onChange={(v) => setShirtStyling({...shirtStyling, collar_height: v})}  options={SHIRT_COLLAR_HEIGHTS}  codePrefix="shirt_collar_height" />
            <StyleSelect label="Collar Stays"   value={shirtStyling.collar_stays}   onChange={(v) => setShirtStyling({...shirtStyling, collar_stays: v})}   options={SHIRT_COLLAR_STAYS}   codePrefix="shirt_collar_stays" />
            <StyleSelect label="Cuff Style"     value={shirtStyling.cuff_style}     onChange={(v) => setShirtStyling({...shirtStyling, cuff_style: v})}     options={SHIRT_CUFF_STYLES}     codePrefix="shirt_cuff_style" />
            <StyleSelect label="Placket"        value={shirtStyling.placket}        onChange={(v) => setShirtStyling({...shirtStyling, placket: v})}        options={SHIRT_PLACKETS}        codePrefix="shirt_placket" />
            <StyleSelect label="Pocket"         value={shirtStyling.pocket}         onChange={(v) => setShirtStyling({...shirtStyling, pocket: v})}         options={SHIRT_POCKETS}         codePrefix="shirt_pocket" />
            <StyleSelect label="Back"           value={shirtStyling.back}           onChange={(v) => setShirtStyling({...shirtStyling, back: v})}           options={SHIRT_BACKS}           codePrefix="shirt_back" />
            <StyleSelect label="Bottom"         value={shirtStyling.bottom}         onChange={(v) => setShirtStyling({...shirtStyling, bottom: v})}         options={SHIRT_BOTTOMS}         codePrefix="shirt_bottom" />
            <StyleSelect label="Monogram"       value={shirtStyling.monogram}       onChange={(v) => setShirtStyling({...shirtStyling, monogram: v})}       options={SHIRT_MONOGRAMS}       codePrefix="shirt_monogram" />
            <ButtonStyleSelect label="Button Style" category="shirt" value={shirtStyling.button_style} onChange={(v) => setShirtStyling({...shirtStyling, button_style: v})} testId="pos-shirt-button-style" />
          </div>

          <AutoStyleFields
            section="shirt"
            styling={shirtStyling}
            setStyling={setShirtStyling}
            excludeFields={[
              "shirt_collar_style","shirt_collar_height","shirt_collar_stays",
              "shirt_cuff_style","shirt_placket","shirt_pocket","shirt_back",
              "shirt_bottom","shirt_button_style","shirt_monogram",
            ]}
            testIdPrefix="pos-shirt-auto-style"
          />

          {/* Custom Style Codes */}
          <CustomStyleCodes
            bucket="shirt"
            value={shirtStyling.custom_codes || []}
            onChange={(v) => setShirtStyling({ ...shirtStyling, custom_codes: v })}
            testId="pos-shirt-custom-codes"
          />

          {/* Remarks */}
          <div className="mt-4">
            <label className="text-xs text-slate-400 block mb-1">Shirt Notes</label>
            <Textarea 
              value={shirtStylingRemarks || ""}
              onChange={(e) => setShirtStylingRemarks(e.target.value)}
              placeholder="Special shirt instructions..."
              className="bg-[#0a1628] border-white/10 h-16 text-sm resize-none"
            />
          </div>
        </div>
      )}

      {/* Order Options */}
      <div className="bg-[#111c2e] rounded-xl p-4 border border-white/10">
        <h3 className="font-bold mb-4">Order Options</h3>
        <div className="space-y-4">
          {/* Rush Order */}
          <div className="flex items-center gap-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <Checkbox 
              id="rush" 
              checked={rushOrder} 
              onCheckedChange={setRushOrder}
              className="border-amber-500 data-[state=checked]:bg-amber-500"
            />
            <label htmlFor="rush" className="flex-1 cursor-pointer">
              <span className="font-medium text-amber-400">🚨 Rush Order</span>
              <span className="text-sm text-slate-400 block">+$100, delivery in 2 weeks</span>
            </label>
          </div>

          {/* General Notes */}
          <div>
            <label className="text-xs text-slate-400 block mb-1">General Order Notes</label>
            <Textarea 
              value={orderNotes || ""}
              onChange={(e) => setOrderNotes(e.target.value)}
              placeholder="Any additional notes for this order..."
              className="bg-[#0a1628] border-white/10 h-20 text-sm resize-none"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MTMStylesStep;
